// GCCV2TabDashboard.tsx
import React from "react";
import { RotateCcw, MessageCircle } from "lucide-react";
import AstriaGCCV2Shell, {
  AstriaGCCV2Tab,
  SHELL_COLORS_GCC_V2,
  getGCCV2Fonts,
} from "./AstriaGCCV2Shell";
import {
  GCCV2ResponseContainer,
  GCCV2Card,
  GCCV2PlainTextCard,
  gccV2LoadingSpinner,
  gccV2SectionKindLabel,
} from "./AstriaGCCV2Card";
import { useGCCV2TabResult } from "./useGCCV2TabResult";
import { GCCV2Language } from "../../../utils/GCC/astriaGCCV2Language";

interface GCCV2TabDashboardProps {
  tab: AstriaGCCV2Tab;
  tabKey: string;
  subCategoryMatch: string;
  buildPrompt: (dob: string, language: GCCV2Language) => string;
  title: Record<GCCV2Language, string>;
  subtitle: Record<GCCV2Language, string>;
  progressCardIndex?: number; // which section index gets a progress bar (e.g. Timing's rhythm card)
  progressLabel?: Record<GCCV2Language, string>;
  companionNoteIndex?: number; // which section index gets a companion-note treatment
  children?: React.ReactNode; // extra content rendered below the cards (e.g. Companion's input box)
}

const COPY = {
  refresh: { en: "Refresh", ar: "تحديث" },
  errorRetry: { en: "Try Again", ar: "حاول مرة أخرى" },
  chat: { en: "Chat", ar: "الدردشة" },
};

const GCCV2TabDashboard: React.FC<GCCV2TabDashboardProps> = ({
  tab,
  tabKey,
  subCategoryMatch,
  buildPrompt,
  title,
  subtitle,
  progressCardIndex,
  progressLabel,
  companionNoteIndex,
  children,
}) => {
  const { loading, error, parsed, language, refresh, goToChat } =
    useGCCV2TabResult({
      tabKey,
      subCategoryMatch,
      buildPrompt,
    });
  const fonts = getGCCV2Fonts(language);

  // Deterministic 40-92 "momentum" indicator derived from the section's own
  // text, since the plain-markdown AI response carries no numeric score to
  // read from (no structured JSON contract exists for GCC v2 today) — this
  // keeps the progress bar visually meaningful/stable per-response rather
  // than a static placeholder number.
  const deriveProgressValue = (text: string): number => {
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
    }
    return 40 + (hash % 53);
  };

  return (
    <AstriaGCCV2Shell active={tab}>
      <div style={{ marginBottom: 20 }}>
        <div
          className="flex items-center justify-between gap-3"
          style={{ marginBottom: 6 }}
        >
          <div
            style={{
              fontSize: 20,
              fontWeight: 600,
              color: SHELL_COLORS_GCC_V2.titleColor,
              fontFamily: fonts.heading,
            }}
          >
            {title[language]}
          </div>
          <button
            onClick={refresh}
            disabled={loading}
            className="flex items-center gap-1.5 rounded-full text-xs font-medium border transition-all hover:bg-black/[0.02] disabled:opacity-50"
            style={{
              borderColor: SHELL_COLORS_GCC_V2.cardBorder,
              color: SHELL_COLORS_GCC_V2.mutedColor,
              background: "transparent",
              paddingInlineStart: 12,
              paddingInlineEnd: 12,
              paddingTop: 7,
              paddingBottom: 7,
            }}
          >
            <RotateCcw className="w-3.5 h-3.5" />
            {COPY.refresh[language]}
          </button>
        </div>
        <p
          style={{
            fontSize: 13.5,
            color: SHELL_COLORS_GCC_V2.mutedColor,
            lineHeight: 1.6,
          }}
        >
          {subtitle[language]}
        </p>
      </div>

      {loading && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "60px 20px",
            gap: 16,
          }}
        >
          {gccV2LoadingSpinner()}
          <p style={{ fontSize: 13, color: SHELL_COLORS_GCC_V2.mutedColor }}>
            {language === "ar"
              ? "نُعِدّ مساحتك بهدوء…"
              : "Quietly preparing your space…"}
          </p>
        </div>
      )}

      {!loading && error && (
        <div
          style={{
            padding: "16px 18px",
            background: "#FBEDE9",
            border: "1px solid #E8C6BB",
            borderRadius: 12,
            color: "#95493A",
            fontSize: 14,
            marginBottom: 16,
          }}
        >
          {error}
          <button
            onClick={refresh}
            style={{
              display: "block",
              marginTop: 10,
              color: SHELL_COLORS_GCC_V2.accent,
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            {COPY.errorRetry[language]}
          </button>
        </div>
      )}

      {!loading && !error && parsed && (
        <GCCV2ResponseContainer>
          {parsed.sections.map((section, idx) =>
            section.kind === "generic" && parsed.sections.length === 1 ? (
              <GCCV2PlainTextCard
                key={idx}
                header={section.heading || title[language]}
                text={section.body}
                delay={idx * 0.08}
                fontFamily={fonts.body}
              />
            ) : (
              <GCCV2Card
                key={idx}
                header={section.heading || gccV2SectionKindLabel(section.kind, language)}
                mainInsight={section.body}
                microText={
                  section.kind === "generic"
                    ? undefined
                    : gccV2SectionKindLabel(section.kind, language)
                }
                delay={idx * 0.08}
                fontFamily={fonts.body}
                alt={idx % 2 === 1}
                progressBar={
                  progressCardIndex === idx
                    ? {
                        value: deriveProgressValue(section.body),
                        label: progressLabel?.[language],
                      }
                    : undefined
                }
                companionNote={
                  companionNoteIndex === idx
                    ? parsed.sections[parsed.sections.length - 1]?.body
                    : undefined
                }
              />
            ),
          )}
        </GCCV2ResponseContainer>
      )}

      {!loading && !error && parsed && (
        <button
          onClick={goToChat}
          className="flex items-center justify-center gap-2 rounded-full text-sm font-semibold text-white transition-all hover:scale-[1.02]"
          style={{
            marginTop: 24,
            width: "100%",
            background: `linear-gradient(135deg, ${SHELL_COLORS_GCC_V2.accent} 0%, #E3C77A 100%)`,
            boxShadow: `0 4px 16px ${SHELL_COLORS_GCC_V2.accent}44`,
            paddingTop: 14,
            paddingBottom: 14,
          }}
        >
          <MessageCircle className="w-4 h-4" />
          {COPY.chat[language]}
        </button>
      )}

      {children}
    </AstriaGCCV2Shell>
  );
};

export default GCCV2TabDashboard;
